import * as React from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react'
import { sb } from '@/lib/supabase'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { BrandMark } from '@/components/brand-mark'

/**
 * Landing page for a clicked magic link.
 *
 * Why this route exists at all: GoTrue composes the emailed link from
 * API_EXTERNAL_URL plus `/verify`, but the tenant's Caddy exposes GoTrue under
 * `/auth/v1/*` and strips that prefix. So the emailed URL
 * (http://host/verify?token=…) does NOT reach GoTrue — it falls through to the
 * SPA. Rather than fight the platform's URL layout, we own /verify and finish
 * the exchange ourselves by POSTing the token to /auth/v1/verify.
 *
 * Two shapes arrive here:
 *   ?token=<hash>&type=magiclink   the emailed link (the normal case)
 *   #access_token=…&refresh_token=…  if GoTrue ever redirects with a session
 *
 * The exchange is deliberately behind a BUTTON rather than running on load.
 * GoTrue issues ONE token for both the emailed link and the 6-digit code, so
 * whatever spends it spends both. This page used to exchange automatically, and
 * corporate mail security fetches links to check them — Microsoft Defender Safe
 * Links does it to every URL in an M365 mailbox. That fetch rendered this page,
 * the page spent the token, and by the time the human clicked or typed the code
 * they got "Token has expired or is invalid". Reproduced exactly: one headless
 * GET of the link, and the code that arrived in the same email was dead.
 *
 * A scanner will happily load this page now. It just won't press anything.
 */
export function Verify() {
  const navigate = useNavigate()
  const [error, setError] = React.useState<string | null>(null)
  const [busy, setBusy] = React.useState(false)

  // Parsed on mount — reading the URL costs nothing and spends nothing.
  const link = React.useMemo(() => {
    const url = new URL(window.location.href)
    const params = url.searchParams
    const hash = new URLSearchParams(url.hash.replace(/^#/, ''))
    return {
      // GoTrue reports its own failures (expired link, bad redirect) as query or
      // fragment params rather than an HTTP error.
      failure:
        params.get('error_description') ??
        hash.get('error_description') ??
        params.get('error') ??
        hash.get('error'),
      accessToken: hash.get('access_token'),
      refreshToken: hash.get('refresh_token'),
      // token_hash carries the identity, so no email is needed — which is what
      // lets the link work in a browser that never saw the sign-in form.
      tokenHash: params.get('token_hash') ?? params.get('token'),
      type: (params.get('type') ?? 'magiclink') as 'magiclink' | 'email' | 'recovery' | 'invite',
    }
  }, [])

  React.useEffect(() => {
    if (link.failure) setError(humanize(link.failure))
  }, [link.failure])

  async function signIn() {
    setBusy(true)
    setError(null)
    try {
      if (link.accessToken && link.refreshToken) {
        const { error: err } = await sb().auth.setSession({
          access_token: link.accessToken,
          refresh_token: link.refreshToken,
        })
        if (err) throw err
      } else if (link.tokenHash) {
        const { error: err } = await sb().auth.verifyOtp({
          token_hash: link.tokenHash,
          type: link.type,
        })
        if (err) throw err
      } else {
        setError('This link is missing its sign-in token. Request a new one.')
        return
      }
      // Drop the token from the address bar before handing over to the app, so
      // it doesn't linger in history or get pasted into a bug report.
      window.history.replaceState({}, '', '/')
      navigate('/', { replace: true })
    } catch (e) {
      setError(humanize(e instanceof Error ? e.message : String(e)))
    } finally {
      setBusy(false)
    }
  }

  const hasToken = Boolean(link.tokenHash || (link.accessToken && link.refreshToken))

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/40 px-4">
      <div className="w-full max-w-sm">
        <div className="mb-7 flex flex-col items-center">
          <BrandMark className="h-11 w-11" />
        </div>
        <Card>
          {error ? (
            <>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <AlertCircle className="h-4 w-4 text-destructive" />
                  Couldn&apos;t sign you in
                </CardTitle>
                <CardDescription>{error}</CardDescription>
              </CardHeader>
              <CardContent>
                <Button className="w-full" onClick={() => navigate('/', { replace: true })}>
                  Back to sign in
                </Button>
              </CardContent>
            </>
          ) : (
            <>
              <CardHeader>
                <CardTitle className="text-base">Finish signing in</CardTitle>
                <CardDescription>
                  {hasToken
                    ? 'One more tap to confirm it was you who opened this link.'
                    : 'This link is missing its sign-in token. Request a new one.'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button className="w-full" onClick={() => void signIn()} disabled={busy || !hasToken}>
                  {busy ? <Loader2 className="animate-spin" /> : <CheckCircle2 />}
                  Sign in
                </Button>
                <Button variant="ghost" className="w-full" onClick={() => navigate('/', { replace: true })}>
                  Use a different email
                </Button>
              </CardContent>
            </>
          )}
        </Card>
      </div>
    </div>
  )
}

function humanize(raw: string): string {
  const s = raw.replace(/\+/g, ' ')
  if (/expired/i.test(s)) return 'That link has expired. Request a new one from the sign-in page.'
  if (/already|used/i.test(s)) return 'That link has already been used. Request a new one.'
  if (/invalid|not found/i.test(s)) return 'That link is no longer valid. Request a new one.'
  if (/redirect/i.test(s)) return 'The link pointed somewhere this app does not allow. Ask an admin to check GOTRUE_URI_ALLOW_LIST.'
  return s
}
